import { type DependencyList, useMemo } from 'react';

import type { EqualFunction } from './types';

import { useCompareMemoize } from './hooks';

/**
 * A hook that works like `useMemo`, but compares dependencies using a custom equality function.
 *
 * @param factory - The factory function to compute the memoized value.
 * @param dependencies - The dependencies array to compare.
 * @param equals - The equality function to use for comparing dependencies.
 * @returns The memoized value.
 */
export const useCompareMemo = <TData = unknown>(
  factory: () => TData,
  dependencies: DependencyList,
  equals: EqualFunction<DependencyList>
) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: the dependencies are memoized using useCompareMemoize
  return useMemo(factory, useCompareMemoize(dependencies, equals));
};
